import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { CheckIcon, ChevronDownIcon } from './icons.js';

export interface ListboxOption {
  value: string;
  label: string;
}

interface ListboxProps {
  value: string;
  options: ListboxOption[];
  onChange: (value: string) => void;
  ariaLabel: string;
  disabled?: boolean;
}

/**
 * A full-width picker for use inside a panel, built the same way as
 * `LanguageSwitcher`'s menu: a native `<select>`'s popup is drawn by the OS
 * and can't be restyled to match the rest of the app, so this is a trigger
 * button plus a listbox of our own, sharing the same keyboard and
 * click-outside behaviour.
 */
export function Listbox({ value, options, onChange, ariaLabel, disabled }: ListboxProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: Event) => {
      const target = event.target;
      if (target instanceof Node && rootRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]')?.focus();
  }, [open]);

  const choose = (next: string) => {
    onChange(next);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const items = [
      ...(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? []),
    ];
    const from = items.indexOf(document.activeElement as HTMLButtonElement);
    const to = (event.key === 'ArrowDown' ? from + 1 : from - 1 + items.length) % items.length;
    items[to]?.focus();
  };

  const selected = options.find((option) => option.value === value);

  return (
    <div className="listbox" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className="listbox__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        <span>{selected?.label ?? value}</span>
        <ChevronDownIcon className="listbox__caret" size={12} />
      </button>

      {open ? (
        <div
          className="listbox__menu"
          ref={menuRef}
          role="listbox"
          aria-label={ariaLabel}
          onKeyDown={onMenuKeyDown}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className="listbox__option"
              onClick={() => choose(option.value)}
            >
              <span>{option.label}</span>
              {option.value === value ? (
                <CheckIcon className="listbox__check" size={14} strokeWidth={2.25} />
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
